import { ClassNameOnlyProps } from 'module/types/common';
import { SingleUserField } from 'module/types/filter-field/field/filterable-field';
import { Select } from '@rocketlane/ui';
import { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Popover } from '../components/components';
import { renderFieldEditorUserAvatar } from '../components/field-editor-user-avatar';
import { UserFieldOptionsGetter } from '../types';
import type { UserOption } from '@rocketlane/ui';
import type { SelectInstance } from 'react-select';

type Props = ClassNameOnlyProps & {
  field: SingleUserField;
  title: ReactNode;
  optionsGetter: UserFieldOptionsGetter;
  onChange: (latest: number | null) => void;
  selectRef: RefObject<SelectInstance>;
};

interface FieldEditorUserOption extends UserOption {
  value: number;
  logoUrl?: string;
}

const StyledPopover = styled(Popover)`
  height: 400px;
`;

export const ControlledSingleUserFieldEditorPopover: FC<
  React.PropsWithChildren<Props>
> = ({ field, title, onChange, optionsGetter, selectRef, className }) => {
  const options = useMemo(() => optionsGetter(field), [field, optionsGetter]);

  const onValueChange = useCallback(
    (latest: FieldEditorUserOption | null) =>
      onChange(field.value === latest?.value ? null : (latest?.value ?? null)),
    [onChange, field],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUser = useMemo(
    () => usersInOptions.find((u) => u.value === field.value),
    [field.value, usersInOptions],
  );

  return (
    <StyledPopover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select<FieldEditorUserOption>
        closeMenuOnSelect
        collapsible={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        menuFlush
        menuIsOpen
        menuPortalTarget={null}
        renderOptionIcon={renderFieldEditorUserAvatar}
        ref={selectRef}
        options={options}
        value={selectedUser}
        onChange={onValueChange}
        searchControl
        variant="user"
        virtualized
      />
    </StyledPopover>
  );
};
